import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { DefaultImageTile } from '@/components/content/default-image-tile';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';

export const CONTENT_DEFAULT_IMAGE_TYPES = [
  'sermon',
  'devotional',
  'announcement',
  'video',
  'gallery',
  'event',
  'service',
] as const;

export type ContentDefaultImageType = (typeof CONTENT_DEFAULT_IMAGE_TYPES)[number];

export interface ContentDefaultImageSlot {
  type: ContentDefaultImageType;
  key: string | null;
  url: string | null;
  platformUrl: string | null;
  effectiveUrl: string | null;
  source: 'church' | 'platform' | null;
}

export const DefaultImagesDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const defaults = useQuery({
    queryKey: ['content', 'default-images'],
    queryFn: () => api.get<{ data: ContentDefaultImageSlot[] }>('/content/default-images'),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (patch: Partial<Record<ContentDefaultImageType, string | null>>) =>
      api.patch<{ data: ContentDefaultImageSlot[] }>('/content/default-images', patch),
    onSuccess: async (result, patch) => {
      queryClient.setQueryData(['content', 'default-images'], result);
      const removed = Object.values(patch).every((value) => value == null);
      toast.success(removed ? t('defaultImages.removed') : t('defaultImages.saved'));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const cropLabels = {
    change: t('defaultImages.change'),
    remove: t('defaultImages.remove'),
    cropTitle: t('defaultImages.cropTitle'),
    zoom: tCommon('media.zoom'),
    apply: t('defaultImages.apply'),
    cancel: tCommon('action.cancel'),
    instructions: t('defaultImages.instructions'),
    closeCropper: tCommon('action.close'),
  };

  const slots = defaults.data?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="sm:max-w-3xl" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('defaultImages.title')}</DialogTitle>
          <DialogDescription>{t('defaultImages.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="max-h-none" contentClassName="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {defaults.isLoading
            ? CONTENT_DEFAULT_IMAGE_TYPES.map((type) => (
                <div key={type} className="space-y-1.5">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="aspect-square w-full" />
                </div>
              ))
            : slots.map((slot) => (
                <DefaultImageTile
                  key={slot.type}
                  label={t(`defaultImages.types.${slot.type}`)}
                  src={slot.effectiveUrl}
                  caption={slot.source === 'platform' ? t('defaultImages.platformFallback') : undefined}
                  canRemove={Boolean(slot.key)}
                  disabled={save.isPending}
                  labels={cropLabels}
                  onUpload={async (blob) => {
                    const { key } = await uploadBlob({ purpose: 'content_image', blob });
                    await save.mutateAsync({ [slot.type]: key });
                  }}
                  onRemove={() => {
                    void save.mutateAsync({ [slot.type]: null });
                  }}
                />
              ))}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
